import { Component, OnInit } from '@angular/core';
import { ClientService } from '../../../../services/client.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { BaseComponent, FeatureFlagService, featurePrompts, Page, promptsDefinitionEvents, UserService } from '@rubii/frontend-common';
import { PromptsManagerComponent } from '@rubii/frontend-common';
import { Prompt, PromptUsage } from '@rubii/entity/src/mdb-entities/ai';
import * as share from '@rubii/share'
import { PromptCategory } from '@rubii/entity/src/mdb-entities/ai/prompt-category.class';

@Component({
  selector: 'app-ai-prompt',
  templateUrl: './ai-prompt.component.html',
  styleUrls: ['./ai-prompt.component.scss']
})
export class AiPromptComponent extends BaseComponent implements OnInit {

  public allPrompts: any[] = [];
  public prompts: (Prompt & { count?: number })[] = [];
  public categories: PromptCategory[] = [];
  public promptUsage: (Pick<PromptUsage, 'promptId'> & { count: number })[] = [];
  public selectedCategoryId: number | null = null;
  public keyword = '';
  public timezone = '';
  public format = '';

  constructor(
    private userService: UserService,
    private client: ClientService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    public user: UserService, 
    private featureService: FeatureFlagService
  ) {
    super();
    this.page = new Page(1, 6, 0);
    this.format = this.user.user.tenant.dateFormat.replace('YYYY', 'yyyy').replace('DD', 'dd') + ' HH:mm:ss OOOO';
    this.timezone = this.user.user.tenant.timezone;
  }

  get userIsAdmin() {
    return this.userService.isAdmin;
  }

  async ngOnInit() {
    await this.loadCategories();
    await this.loadPrompts();
    await this.loadPromptUsage();
  }

  async loadCategories() {
    try {
      this.categories = await this.client.ai.listPromptCategories();
    } catch (error) {
      console.error('Error loading categories:', error);
      this.openSnackBarWarning(this.snack, 'Failed to load categories', 'close');
    }
  }

  async loadPrompts() {
    try {
      this.allPrompts = await this.client.ai.listPrompts();
      this.paginate();
    } catch (error) {
      console.error('Error loading prompts:', error);
      this.openSnackBarWarning(this.snack, 'Failed to load prompts', 'close');
    }
  }

  async loadPromptUsage() {
    try {
      this.promptUsage = (await this.client.ai.getPromptCountByUser()) as unknown as (Pick<PromptUsage, 'promptId'> & { count: number })[];
      this.allPrompts.forEach(prompt => {
        prompt.count = this.promptUsage.find(p => p.promptId === prompt._id)?.count || 0;
      });
    } catch (error) {
      console.error('Error loading prompt usage:', error);
      this.openSnackBarWarning(this.snack, 'Failed to load prompt usage', 'close');
    }
  }

  paginate() {
    let filteredPrompts = this.allPrompts;

    // Filter by category
    if (this.selectedCategoryId !== null) {
      filteredPrompts = filteredPrompts.filter(prompt => prompt.categoryId === this.selectedCategoryId);
    }

    // Filter by search keyword
    if (this.keyword) {
      const searchKeyword = this.keyword.toLowerCase();
      filteredPrompts = filteredPrompts.filter(prompt => 
        prompt.name.toLowerCase().includes(searchKeyword) ||
        prompt.description.toLowerCase().includes(searchKeyword) ||
        prompt.prompt.toLowerCase().includes(searchKeyword)
      );
    }

    this.page.total = filteredPrompts.length;
    
    if (filteredPrompts.length === 0) {
      this.page.pageId = 1;
      this.prompts = [];
    } else {
      this.page.pageId = Math.min(this.page.pageId, Math.ceil(filteredPrompts.length / this.page.pageSize));
      this.prompts = filteredPrompts.slice((this.page.pageId - 1) * this.page.pageSize, this.page.pageId * this.page.pageSize);
    }
  }

  search(keyword: string) {
    this.keyword = keyword;
    this.page.pageId = 1;
    this.paginate();
  }

  filterByCategory(categoryId: number | null) {
    this.selectedCategoryId = categoryId;
    this.page.pageId = 1;
    this.paginate();
  }

  pageChanged(event: PageEvent) {
    this.page.pageId = event.pageIndex + 1;
    this.page.pageSize = event.pageSize;
    this.paginate();
  }

  getCategoryName(categoryId: number): string {
    const category = this.categories.find(cat => cat._id === categoryId);
    return category ? category.name : 'Unknown';
  }

  createOrUpdatePrompt(prompt?: Prompt) {
    // For now, we'll use a simple alert. This will be replaced with the actual PromptsManagerComponent
    // alert('Create new prompt functionality will be implemented with lib-prompts-manager component');
    // TODO: Implement with PromptsManagerComponent from @rubii/frontend-common
    const ref = this.dialog.open(PromptsManagerComponent, {
      data: {
        selectedPromptCategory: prompt?.categoryId,
        selectedPrompt: prompt?._id,
        featureEvents: {
          create: promptsDefinitionEvents.PromptLibraryHomePageCreate,
          edit: promptsDefinitionEvents.PromptLibraryHomePageEdit,
          delete: promptsDefinitionEvents.PromptLibraryHomePageDelete
        }
      },
      width: '800px',
      height: 'calc(100vh - 200px)',
      panelClass: 'prompt-library-dialog'
    });

    ref.componentInstance.updatedPrompt.subscribe((newPrompt: Prompt) => {
      console.log('Prompt updated:', newPrompt);
      if (!prompt) {
        this.prompts.push(newPrompt);
      } else {
        Object.assign(prompt, newPrompt);
      }
      this.dialog.closeAll();
    });

    ref.afterClosed().subscribe(result => {
    });
  }

  async duplicatePrompt(prompt: any) {
    try {
      // Generate unique name by checking existing prompts
      let duplicateName = `Copy of ${prompt.name}`;
      let counter = 2;
      
      // Check if "Copy of X" already exists, then try "Copy 2 of X", "Copy 3 of X", etc.
      while (this.allPrompts.some(p => p.name === duplicateName)) {
        duplicateName = `Copy ${counter} of ${prompt.name}`;
        counter++;
      }
      
      const duplicatedPrompt = {
        _id: share.helpers.number.generateUniqIdFromString(duplicateName + Date.now(), 99),
        name: duplicateName,
        description: prompt.description,
        prompt: prompt.prompt,
        categoryId: prompt.categoryId,
        createdby: this.user.user.id,
        createtime: Date.now(),
        createdate: new Date().toISOString()
      };

      // Create the prompt via API
      const newPrompt = await this.client.ai.createPrompt(duplicatedPrompt);

      console.log(`triggering create event (duplicate)`, promptsDefinitionEvents.PromptLibraryHomePageCreate);
      await this.featureService.trigger(featurePrompts, promptsDefinitionEvents.PromptLibraryHomePageCreate, newPrompt);
      
      // Find the index of the original prompt in the current view
      const originalIndex = this.prompts.findIndex(p => p._id === prompt._id);
      
      // Add to allPrompts array
      this.allPrompts.push(newPrompt);
      
      // If original prompt is visible in current page, insert the duplicate right after it
      if (originalIndex !== -1) {
        this.prompts.splice(originalIndex + 1, 0, newPrompt);
      } else {
        // If original prompt is not on current page, refresh the data
        this.paginate();
      }
      
      this.openSnackBar(this.snack, 'Prompt duplicated successfully', 'close');
    } catch (error) {
      console.error('Error duplicating prompt:', error);
      this.openSnackBarWarning(this.snack, 'Failed to duplicate prompt', 'close');
    }
  }

  async deletePrompt(prompt: any) {
    if (confirm(`Are you sure you want to delete the prompt "${prompt.name}"?`)) {
      try {
        await this.client.ai.deletePrompt({ _id: prompt._id });
        console.log(`triggering delete event`, promptsDefinitionEvents.PromptLibraryHomePageDelete);
        await this.featureService.trigger(featurePrompts, promptsDefinitionEvents.PromptLibraryHomePageDelete, prompt);
        this.openSnackBar(this.snack, 'Prompt deleted successfully', 'close');
        await this.loadPrompts();
      } catch (error) {
        console.error('Error deleting prompt:', error);  
        this.openSnackBarWarning(this.snack, 'Failed to delete prompt', 'close');
      }
    }
  }
}